/**
 * CardWidget Component API
 *
 * A shared component for card-based widgets like Trending Deals and Recently Viewed.
 * Provides consistent structure, styling, and behavior across all card widgets.
 */

import { ReactNode } from 'react';

/**
 * Props for the CardWidget component
 */
export interface CardWidgetProps {
  /**
   * Widget title displayed in the header
   */
  title: string;

  /**
   * Optional icon/emoji to display before the title
   */
  icon?: string;

  /**
   * Optional CSS class for custom styling
   */
  className?: string;

  /**
   * Header action link (e.g., "View All")
   */
  actionLink?: {
    text: string;
    href: string;
    onClick?: (e: React.MouseEvent) => void;
  };

  /**
   * Additional header actions (e.g., clear button)
   */
  headerActions?: ReactNode;

  /**
   * Main content of the widget
   */
  children: ReactNode;

  /**
   * Loading state configuration
   */
  loading?: {
    isLoading: boolean;
    message?: string;
    spinner?: boolean;
  };

  /**
   * Error state configuration
   */
  error?: {
    message: string;
    retry?: () => void;
  };

  /**
   * Empty state configuration
   */
  empty?: {
    message: string;
    hint?: string;
    icon?: string;
    action?: {
      text: string;
      onClick: () => void;
    };
  };

  /**
   * Footer content (optional)
   */
  footer?: ReactNode;

  /**
   * Whether to show a border at the bottom of the header
   */
  headerDivider?: boolean;

  /**
   * Accessibility label for the widget
   */
  ariaLabel?: string;

  /**
   * Test ID for automated testing
   */
  testId?: string;
}

/**
 * Specific props for TrendingDeals widget
 */
export interface TrendingDealsWidgetProps extends Omit<CardWidgetProps, 'title' | 'icon'> {
  /**
   * Number of deals to display
   */
  limit?: number;

  /**
   * Whether to show vote counts
   */
  showVotes?: boolean;
}

/**
 * Specific props for RecentlyViewed widget
 */
export interface RecentlyViewedWidgetProps extends Omit<CardWidgetProps, 'title' | 'icon'> {
  /**
   * Whether to show the clear history button
   */
  showClearButton?: boolean;

  /**
   * Whether to show the login prompt for anonymous users
   */
  showLoginPrompt?: boolean;

  /**
   * Current deal ID to exclude from the list
   */
  excludeDealId?: string;
}

/**
 * Common loading states
 */
export const WIDGET_LOADING_STATES = {
  TRENDING: {
    isLoading: true,
    message: 'Loading trending deals...',
    spinner: true,
  },
  RECENTLY_VIEWED: {
    isLoading: true,
    message: 'Loading history...',
    spinner: true,
  },
  MOST_SAVED: {
    isLoading: true,
    message: 'Loading saved deals...',
    spinner: true,
  },
} as const;

/**
 * Common empty states
 */
export const WIDGET_EMPTY_STATES = {
  TRENDING: {
    message: 'No trending deals available',
    hint: 'Check back soon for popular deals',
    icon: '🔥',
  },
  RECENTLY_VIEWED: {
    message: 'No recently viewed deals',
    hint: 'Your viewing history will appear here',
    icon: '👁️',
  },
  MOST_SAVED: {
    message: 'No saved deals yet',
    hint: 'Save deals to see them here',
    icon: '💾',
  },
} as const;
